import { z } from "zod";

import { MetaDataSchema } from "@/types/meta-data";

export const EmailSchema = z.object({
	id: z.number(),
	email: z.string().email("Please enter a valid email address"),
	accountId: z.string().optional().nullable(),
	provider: z.string(),
	type: z.string(),
	limitPerDay: z
		.number()
		.min(1, "Limit per day must be greater than 0")
		.max(500, "Limit per day cannot exceed 500"),
	signature: z.string().optional().nullable(),
	active: z.boolean(),
	senderFirstName: z.string(),
	senderLastName: z.string(),
	status: z.string(),
	createdAt: z.string().optional().nullable(),
});

export const EmailsResponseSchema = z.object({
	data: z.array(EmailSchema),
	meta: MetaDataSchema,
});

export type Email = z.infer<typeof EmailSchema>;
export type EmailsResponse = z.infer<typeof EmailsResponseSchema>;

/**
 * Email statuses that mean the account can no longer send and the user must
 * recover it from the dashboard. CREDENTIALS reconnects the existing account;
 * DELETED was removed on Unipile's side and needs a fresh login. Both surface
 * the same "logged out" UI + recovery button.
 */
export const LOGGED_OUT_EMAIL_STATUSES = ["CREDENTIALS", "DELETED"];

export const isEmailLoggedOut = (status?: string | null): boolean =>
	!!status && LOGGED_OUT_EMAIL_STATUSES.includes(status);

export const SendTestSignatureRequestSchema = z.object({
	emailId: z.number(),
	recipientEmail: z
		.string()
		.transform((val) => (val === "" ? undefined : val))
		.pipe(z.string().email().optional()),
	testMessage: z.string().min(1, "Test message is required"),
});

export const SendTestSignatureResponseSchema = z.object({
	success: z.boolean(),
	message: z.string(),
});

export type SendTestSignatureFormValues = z.input<typeof SendTestSignatureRequestSchema>;
export type SendTestSignatureRequest = z.output<typeof SendTestSignatureRequestSchema>;
export type SendTestSignatureResponse = z.infer<typeof SendTestSignatureResponseSchema>;
